"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useRef, useState, useTransition } from "react";
import type { FetchListingFn } from "./listing.types";
interface UseListingStateOptions<TItem> {
  /** Server-rendered first page — seeds `items` and `total` without an extra fetch. */
  initialItems: TItem[];
  /** Server-rendered total count for the initial unfiltered query. */
  initialTotal: number;
  /** Page size used for both filter-driven refetches and load-more. */
  pageSize: number;
  /** Server-action fetcher invoked on filter / search / load-more. */
  fetchMore: FetchListingFn<TItem>;
}
interface ListingState<TItem> {
  items: TItem[];
  total: number;
  selectedCategoryId: string | null;
  searchQuery: string | null;
  isPending: boolean;
  /** True when more items are available beyond what's currently loaded. */
  hasMore: boolean;
  /**
   * Switch the active category and refetch at offset 0 with the current
   * search query. Pass `null` to clear the filter.
   */
  setSelectedCategoryId: (categoryId: string | null) => void;
  /**
   * Update the active search query and refetch at offset 0 with the
   * current category. Pass `null` (or empty string) to clear search.
   * Listings without search just don't call this.
   */
  setSearchQuery: (searchQuery: string | null) => void;
  /** Append the next page of items at the current filter state. */
  loadMore: () => void;
}

/**
 * State + fetch orchestrator for filterable / paginated directory
 * listings. Centralises the request-id guard (so stale responses can't
 * overwrite fresher state when filters change rapidly) and the
 * callback-driven refetch pattern.
 *
 * Used by `CommercialTenantListing` (with search) and `ResourceListing`
 * (no search — just ignores `setSearchQuery` / `searchQuery`).
 */
export function useListingState(t0) {
  const $ = _c(25);
  const {
    initialItems,
    initialTotal,
    pageSize,
    fetchMore
  } = t0;
  const [items, setItems] = useState(initialItems);
  const [total, setTotal] = useState(initialTotal);
  const [selectedCategoryId, setSelectedCategoryIdState] = useState(null);
  const [searchQuery, setSearchQueryState] = useState(null);
  const [isPending, startTransition] = useTransition();
  const requestIdRef = useRef(0);
  let t1;
  if ($[0] !== fetchMore || $[1] !== pageSize) {
    t1 = (nextCategoryId, nextSearchQuery) => {
      const requestId = requestIdRef.current = requestIdRef.current + 1;
      startTransition(async () => {
        ;
        try {
          const fresh = await fetchMore({
            categoryId: nextCategoryId,
            searchQuery: nextSearchQuery,
            offset: 0,
            limit: pageSize
          });
          if (requestId !== requestIdRef.current) {
            return;
          }
          setItems(fresh.items);
          setTotal(fresh.total);
        } catch (t2) {
          const error = t2;
          if (requestId === requestIdRef.current) {
            console.error("useListingState refetch failed", error);
          }
        }
      });
    };
    $[0] = fetchMore;
    $[1] = pageSize;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const refetch = t1;
  let t2;
  if ($[3] !== refetch || $[4] !== searchQuery) {
    t2 = categoryId => {
      setSelectedCategoryIdState(categoryId);
      refetch(categoryId, searchQuery);
    };
    $[3] = refetch;
    $[4] = searchQuery;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const setSelectedCategoryId = t2;
  let t3;
  if ($[6] !== refetch || $[7] !== selectedCategoryId) {
    t3 = next => {
      const normalized = next?.trim() || null;
      setSearchQueryState(normalized);
      refetch(selectedCategoryId, normalized);
    };
    $[6] = refetch;
    $[7] = selectedCategoryId;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const setSearchQuery = t3;
  let t4;
  if ($[9] !== fetchMore || $[10] !== items.length || $[11] !== pageSize || $[12] !== searchQuery || $[13] !== selectedCategoryId) {
    t4 = () => {
      const requestId_0 = requestIdRef.current = requestIdRef.current + 1;
      startTransition(async () => {
        ;
        try {
          const next_0 = await fetchMore({
            categoryId: selectedCategoryId,
            searchQuery,
            offset: items.length,
            limit: pageSize
          });
          if (requestId_0 !== requestIdRef.current) {
            return;
          }
          setItems(prev => [...prev, ...next_0.items]);
          setTotal(next_0.total);
        } catch (t5) {
          const error_0 = t5;
          if (requestId_0 === requestIdRef.current) {
            console.error("useListingState load-more failed", error_0);
          }
        }
      });
    };
    $[9] = fetchMore;
    $[10] = items.length;
    $[11] = pageSize;
    $[12] = searchQuery;
    $[13] = selectedCategoryId;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const loadMore = t4;
  const t5 = items.length < total;
  let t6;
  if ($[15] !== isPending || $[16] !== items || $[17] !== loadMore || $[18] !== searchQuery || $[19] !== selectedCategoryId || $[20] !== setSearchQuery || $[21] !== setSelectedCategoryId || $[22] !== t5 || $[23] !== total) {
    t6 = {
      items,
      total,
      selectedCategoryId,
      searchQuery,
      isPending,
      hasMore: t5,
      setSelectedCategoryId,
      setSearchQuery,
      loadMore
    };
    $[15] = isPending;
    $[16] = items;
    $[17] = loadMore;
    $[18] = searchQuery;
    $[19] = selectedCategoryId;
    $[20] = setSearchQuery;
    $[21] = setSelectedCategoryId;
    $[22] = t5;
    $[23] = total;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  return t6;
}